import { useEffect, useRef } from "react";
import { fetchDueReminders, fetchReminders, requestAvatarMessage, ackReminder } from "@/lib/api";
import { playRing } from "@/lib/audio";
import { fireMochiNotification } from "@/lib/mochiNotifications";

// Polls the backend for reminder alerts every 15s. The backend returns a list
// of `{reminder, kind}` items where `kind` is either:
//   - "pre" → 15-minute heads-up (fires once when we cross T-15min)
//   - "due" → the reminder's actual time has hit
// The backend atomically marks each reminder so an alert never double-fires.
// We hand each alert to the host (App.js) via `onReminderDue` which drives
// the popup, browser notification, and audio ring.
//
//   useReminderPolling({ today, onReminderDue, onRefreshReminders });
export function useReminderPolling({ today, onReminderDue, onRefreshReminders }) {
    const alertedRef = useRef(new Set());

    useEffect(() => {
        let cancelled = false;

        const tick = async () => {
            let alerts = [];
            try {
                alerts = await fetchDueReminders();
            } catch (e) {
                // High-frequency poll — debug level so an offline tab doesn't spam warnings.
                console.debug("reminder poll failed (will retry)", e);
                return;
            }
            if (cancelled || !alerts.length) return;

            for (const item of alerts) {
                if (cancelled) break;
                const r = item.reminder;
                const kind = item.kind; // "pre" | "due"
                const dedupKey = `${r.id}:${kind}`;
                if (alertedRef.current.has(dedupKey)) continue;
                alertedRef.current.add(dedupKey);

                const isPre = kind === "pre";
                let body = isPre
                    ? `Heads up — ${r.title} in 15 min ⏳`
                    : `Reminder: ${r.title} ⏰`;
                try {
                    const ai = await requestAvatarMessage(
                        isPre ? "reminder_pre_alert" : "reminder_due",
                        isPre ? "worried" : "energetic",
                        { task_title: r.title, time: r.time },
                    );
                    if (ai?.message) body = ai.message;
                } catch (e) { console.warn("reminder AI line failed — using fallback body", e); }

                onReminderDue?.({
                    id: r.id,
                    title: r.title,
                    body,
                    kind,
                });

                if ("Notification" in window && Notification.permission === "granted") {
                    // Pick the phrase bank based on snooze ladder position.
                    // count=0 → initial, 1 → first retry, 2 → final retry.
                    const sc = Number(r.snooze_count || 0);
                    const phraseType = isPre
                        ? "reminder_pre"
                        : sc === 1 ? "reminder_retry_1"
                        : sc >= 2 ? "reminder_retry_2"
                        : "reminder_due";
                    const notif = fireMochiNotification(
                        phraseType,
                        { task_title: r.title },
                        { tag: `reminder-${kind}-${r.id}` },
                    );
                    // Tapping the notification = user opened app from it.
                    // Cancel the auto-snooze ladder for this fire cycle.
                    if (notif && !isPre) {
                        notif.onclick = () => {
                            // window.focus can throw in some iframe contexts.
                            try { window.focus(); } catch { /* focus unsupported — silent ok */ }
                            ackReminder(r.id).catch((e) => console.warn("reminder ack failed", e));
                            notif.close();
                        };
                    }
                }

                // Softer single ring for pre-alert; full ring for actual due.
                try { playRing(isPre ? 1 : 3); } catch { /* audio not yet unlocked — silent ok */ }

                fetchReminders(today).then(onRefreshReminders).catch((e) => console.warn("reminder list refresh failed", e));
                // One alert per tick to avoid stacking popups
                break;
            }
        };

        const id = setInterval(tick, 15_000);
        tick();
        const onGesture = () => tick();
        const onVisible = () => { if (!document.hidden) tick(); };
        const onFocus = () => tick();
        window.addEventListener("pointerdown", onGesture, { once: true });
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", onFocus);

        return () => {
            cancelled = true;
            clearInterval(id);
            window.removeEventListener("pointerdown", onGesture);
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", onFocus);
        };
    }, [today, onReminderDue, onRefreshReminders]);
}
